import { G } from '@mobily/ts-belt';
import { useMemo } from 'react';
import { SelectInput, SelectInputProps, useGetList, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { buildCampaignPlanOptions } from '@Common/bo/b2b_campaign_plan_options';
import { doesCampaignTypeAcceptPayout } from '@Common/bo/b2b_campaigns';
import {
  B2bSavingPlanDeductibility,
  B2bSavingPlanType,
  isFullyManagedPlan,
} from '@Common/types/b2b/plans';
import { B2bCompanyPlan, B2bCompanyPlansQuery } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';

import { AllocationRecord } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { useResourceBasePath, useWatchObject } from '@sh4res/bo-common/hooks';

type B2bCompanyPlanSelectInputProps = Omit<SelectInputProps, 'choices' | 'filter' | 'source'> & {
  filter: B2bCompanyPlansQuery['filter'];
  choiceId: number;
  allocationId: number;
  contractIds: number[];
  optionText?: (record: B2bCompanyPlan) => string;
  onPlanChange?: (choiceId: number) => void;
  restrictedToPayoutOnly?: boolean;
  excludeDeductiblePlans?: boolean;
  'data-testid': string;
};

type Choice = {
  id: string;
  plan_id: number | 'payout' | 'ccb';
  name: string;
  type: 'payout' | 'plan' | 'managed_plan' | 'ccb';
  disabled: boolean;
  deductibility?: B2bSavingPlanDeductibility;
  planType: B2bSavingPlanType | null;
  planLabel?: string | null;
};

const optionTextDefault = (option: B2bCompanyPlan) => option.name;

// If allocation choice is managed_plan only allow one entry per plan_id and by deductibility if voluntary_deposit
function isDisabled({
  choice,
  optionId,
  type,
  choices,
  planId,
  deductibility,
}: {
  choice: string;
  optionId: string;
  type: 'payout' | 'plan' | 'managed_plan';
  choices: AllocationRecord['company_collected_choices'];
  planId: number;
  deductibility: B2bSavingPlanDeductibility | null;
}): boolean {
  return (
    choice !== optionId &&
    type === 'managed_plan' &&
    !!(choices ?? []).find(
      (entry: NonNullable<AllocationRecord['company_collected_choices']>[number]) =>
        entry.plan_id === planId && (!deductibility || entry.deductibility === deductibility),
    )
  );
}

export const B2bCompanyPlanSelectInput = ({
  optionText = optionTextDefault,
  allocationId,
  contractIds,
  choiceId,
  filter,
  onPlanChange,
  restrictedToPayoutOnly = false,
  excludeDeductiblePlans = false,
  'data-testid': dataTestId,
  ...props
}: B2bCompanyPlanSelectInputProps) => {
  const { setValue } = useFormContext();
  const resourceBasePath = useResourceBasePath();
  const translate = useTranslate();
  const source = `allocations.${allocationId}.company_collected_choices.${choiceId}.id`;
  const choice = useWatch({ name: source });
  const choices = useWatch({ name: `allocations.${allocationId}.company_collected_choices` });
  const {
    type: campaignType,
    matching_contribution_related_cash_source: matchingContributionRelatedCashSource,
  } = useWatchObject<B2bCampaignFormValues>()([
    'type',
    'matching_contribution_related_cash_source',
  ]);
  const { data = [], isLoading } = useGetList<B2bCompanyPlan>(
    `${resourceBasePath}companies/plans`,
    {
      filter,
      sort: { field: 'created_at', order: 'DESC' },
      pagination: { page: 1, perPage: 100 },
    },
  );

  const instrumentIds = useMemo(
    () => data.flatMap((plan) => plan.shares_plans.flatMap((sp) => sp.instruments_universe)),
    [data],
  );

  const { data: ccbInstrumentsLinkedToPlans } = useGetList<GetInstrumentsResponseItem>(
    'instruments',
    {
      filter: {
        id: instrumentIds,
        type: 'ccb',
      },
      sort: { field: 'id', order: 'DESC' },
      pagination: { page: 1, perPage: instrumentIds.length },
    },
    {
      enabled: instrumentIds.length > 0,
    },
  );

  const plans = useMemo(() => data.flatMap((plan) => plan.shares_plans), [data]);

  const planIdContainingCcbInstruments = useMemo(
    () =>
      plans.find((plan) =>
        ccbInstrumentsLinkedToPlans
          ?.map((ccb) => ccb.id)
          .some((ccbInstrumentId) => plan.instruments_universe.includes(ccbInstrumentId)),
      )?.id ?? null,
    [plans, ccbInstrumentsLinkedToPlans],
  );

  // Built from all the campaign contracts, so that the names are the same as in the allocation file
  const planOptions = useMemo(
    () =>
      buildCampaignPlanOptions({
        products: data,
        campaignType,
        matchingContributionRelatedCashSource,
      }),
    [data, campaignType, matchingContributionRelatedCashSource],
  );

  const flattenedChoices: Choice[] = useMemo(
    () =>
      planOptions
        .filter(
          ({ product, deductibility }) =>
            // allow selecting plans only with matching affiliation
            contractIds.includes(product.b2b_contract_id) &&
            // Employees aged 70+ can no longer subscribe to deductible plans
            !(deductibility === 'deductible' && excludeDeductiblePlans),
        )
        .map(({ id, name, sharesPlan, deductibility }): Choice => {
          const type = isFullyManagedPlan(sharesPlan.type)
            ? ('managed_plan' as const)
            : ('plan' as const);
          return {
            id,
            plan_id: sharesPlan.id,
            name,
            deductibility: deductibility ?? undefined,
            type,
            disabled: isDisabled({
              choice,
              optionId: id,
              type,
              choices,
              planId: sharesPlan.id,
              deductibility,
            }),
            planType: sharesPlan.type,
            planLabel: sharesPlan.plan_label,
          };
        })
        .concat(
          campaignType === 'participation' && G.isNotNullable(planIdContainingCcbInstruments)
            ? [
                {
                  id: `ccb_${planIdContainingCcbInstruments}`,
                  plan_id: 'ccb',
                  name: translate('resources.bo_common.b2b.campaigns.sections.allocations.ccb'),
                  type: 'plan' as const,
                  disabled: false,
                  planType: null,
                },
              ]
            : [],
        )
        .concat(
          doesCampaignTypeAcceptPayout(campaignType) || restrictedToPayoutOnly
            ? [
                {
                  id: 'payout',
                  plan_id: 'payout',
                  name: translate('resources.bo_common.b2b.campaigns.sections.allocations.payouts'),
                  type: 'payout' as const,
                  disabled: false,
                  planType: null,
                },
              ]
            : [],
        ),
    [
      planOptions,
      translate,
      choice,
      choices,
      campaignType,
      contractIds,
      planIdContainingCcbInstruments,
      restrictedToPayoutOnly,
      excludeDeductiblePlans,
    ],
  );

  return (
    <SelectInput
      {...props}
      source={source}
      optionText={optionText}
      choices={flattenedChoices}
      isLoading={isLoading}
      data-testid={dataTestId}
      onChange={(event) => {
        const selectedId = event.target.value;

        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.instrument_id`,
          null,
        );
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.risk_profile`,
          null,
        );
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.id`,
          selectedId,
        );

        const choice = flattenedChoices.find((choice) => choice.id === selectedId);
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.plan_id`,
          choice?.plan_id ?? null,
        );
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.plan_type`,
          choice?.planType ?? null,
        );
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.plan_label`,
          choice?.planLabel ?? null,
        );
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.allocation_type`,
          choice?.type ?? null,
        );
        setValue(
          `allocations.${allocationId}.company_collected_choices.${choiceId}.deductibility`,
          choice?.deductibility ?? null,
        );
        onPlanChange?.(choiceId);
      }}
    />
  );
};
